import { IconName } from "@/components/ui/Icon";
import { PRO_EDITOR_ROLES } from "@/constants/roles";
import { UserRole } from "@/types/auth";
import { PATHS } from "./paths";

export interface NavPage {
  label: string;
  path: string;
  // Sem ícone = subitem (recuado) do item anterior.
  icon?: IconName;
  // Prefixo que também marca o item como ativo (ex.: detalhes de tags).
  matchPrefix?: string;
  // Item-pai de subitens: fica destacado (sem preenchimento) quando um
  // subitem está ativo.
  group?: boolean;
  roles?: UserRole[];
}

export interface NavModule {
  key: "inicio" | "painel" | "analytics" | "pro" | "social" | "mentoria" | "integracoes" | "conta";
  label: string;
  // Sobretítulo e título do menu lateral.
  kicker: string;
  title: string;
  pages: NavPage[];
  // Módulos sem página ainda aparecem no topo com o selo "Em breve".
  comingSoon?: boolean;
  // Não aparece no menu superior (ex.: Minha conta, aberta pelo botão da conta).
  hidden?: boolean;
  roles?: UserRole[];
}

const ADMIN_FINANCE: UserRole[] = ["account-admin", "finance-manager"];

export const NAV_MODULES: NavModule[] = [
  {
    key: "inicio",
    label: "Início",
    kicker: "",
    title: "",
    pages: [{ label: "Início", path: PATHS.home }],
  },
  {
    key: "painel",
    label: "Seu negócio",
    kicker: "Painel",
    title: "Seu negócio",
    pages: [
      { label: "Visão geral", path: PATHS.overview, icon: "grid" },
      { label: "Gerador de link", path: PATHS.linkGenerator, icon: "link" },
    ],
  },
  {
    key: "analytics",
    label: "Analytics",
    kicker: "Yuha! Analytics",
    title: "Métricas",
    pages: [
      { label: "Seu site", path: PATHS.site, icon: "globe" },
      { label: "Google Ads", path: PATHS.googleAds, icon: "mega", group: true, matchPrefix: PATHS.googleAds },
      { label: "Visão geral", path: PATHS.googleAds },
      { label: "Campanhas", path: PATHS.googleAdsCampaigns },
      { label: "Meta Ads", path: PATHS.metaAds, icon: "target", group: true, matchPrefix: PATHS.metaAds },
      { label: "Visão geral", path: PATHS.metaAds },
      { label: "Campanhas", path: PATHS.metaAdsCampaigns },
      { label: "Tags", path: PATHS.tags, icon: "tag", matchPrefix: PATHS.tags },
      {
        label: "Funil de vendas",
        path: PATHS.funnelReports,
        icon: "funnel",
        group: true,
        matchPrefix: "/dash/analytics/funil-de-vendas/",
      },
      { label: "Relatórios", path: PATHS.funnelReports },
      { label: "Formulários de captura", path: PATHS.funnelForms },
    ],
  },
  {
    key: "pro",
    label: "Analytics Pro",
    kicker: "Yuha! Analytics Pro",
    title: "Análises",
    pages: [
      { label: "Suas análises", path: PATHS.proAnalyses, icon: "bars", matchPrefix: PATHS.proAnalyses },
      { label: "Configuração de IA", path: PATHS.proAi, icon: "spark", roles: PRO_EDITOR_ROLES },
    ],
  },
  {
    key: "social",
    label: "Social",
    kicker: "Yuha! Social",
    title: "Vitrine",
    pages: [
      { label: "Início", path: PATHS.showcaseHome, icon: "home" },
      { label: "Cabeçalho", path: PATHS.showcaseHeader, icon: "user" },
      { label: "Editor da vitrine", path: PATHS.showcaseEditor, icon: "store" },
      { label: "Temas", path: PATHS.showcaseThemes, icon: "brush" },
      { label: "Relatórios", path: PATHS.showcaseReports, icon: "bars" },
    ],
  },
  {
    key: "mentoria",
    label: "MentorIA",
    kicker: "MentorIA",
    title: "Assistente",
    pages: [],
    comingSoon: true,
  },
  {
    key: "integracoes",
    label: "Integrações",
    kicker: "Integrações",
    title: "Conexões",
    roles: ["account-admin"],
    pages: [
      { label: "Código de monitoramento", path: PATHS.trackingCode, icon: "code" },
      { label: "Contas integradas", path: PATHS.connectedAccounts, icon: "plug" },
    ],
  },
  {
    key: "conta",
    label: "Minha conta",
    kicker: "Minha conta",
    title: "Configurações",
    hidden: true,
    pages: [
      { label: "Assinatura", path: PATHS.subscription, icon: "card" },
      { label: "Planos e produtos", path: PATHS.plans, icon: "store", roles: ADMIN_FINANCE },
      { label: "Dados cadastrais", path: PATHS.accountData, icon: "doc", roles: ADMIN_FINANCE },
      { label: "Usuários", path: PATHS.users, icon: "users", roles: ["account-admin"] },
      { label: "Meu perfil", path: PATHS.profile, icon: "user" },
    ],
  },
];

export const canSee = (roles: UserRole[] | undefined, userRoles: UserRole[]) =>
  !roles || roles.some((role) => userRoles.includes(role));

const isPageActive = (page: NavPage, pathname: string) =>
  pathname === page.path || (!!page.matchPrefix && pathname.startsWith(page.matchPrefix));

export const findActiveModule = (pathname: string): NavModule | undefined =>
  NAV_MODULES.find((module) => module.pages.some((page) => isPageActive(page, pathname)));

export const isNavPageActive = isPageActive;
